import React, { useState } from 'react';
import { Switch } from 'antd';
import { useNavigate } from 'react-router-dom';
import { MoreVertical, Camera, Video, Image, Settings } from 'lucide-react';
import { DeleteIcon, EditIcon } from '../../assets/svgs/icons';
import { useSelector } from 'react-redux';
import { selectCurrentRole } from '../../store/auth/authSlice';
import UserGradientAvatar from './UserGradientAvatar';

// Questions Card View Component


const QuestionCards = ({ data, navigate, onDelete }) => {
  const userRole = useSelector(selectCurrentRole);
  const [expandedCards, setExpandedCards] = useState({});

  const toggleDescription = (itemId) => {
    setExpandedCards(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {data.map((item, index) => (
        <div key={item._id || item.key} className="bg-white rounded-xl border p-4 flex flex-col ">
          <div className="flex items-center justify-between mb-0">
            <div className="flex items-center gap-2 ">
              <div className="flex relative">
                <MoreVertical className="" size={20} />
                <MoreVertical className=" relative -left-3" size={20} />
              </div>
              <span className="text-gray-600">{item.number || item.srNo}</span>
            </div>
            <div className="flex items-center gap-2">
              {userRole === 'admin' && (
                <button 
                  className="p-2 border-0 rounded-lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onDelete && onDelete(item);
                  }}
                >
                  <DeleteIcon className="text-red-500" size={18} />
                </button>
              )}
              {userRole === 'admin' && (
                <button 
                  className="p-2 border-0 rounded-lg"
                  onClick={() => navigate(`/${window.location.pathname.split('/')[1]}/forms/${item.form}/questions/edit/${item._id}`)}
                >
                  <EditIcon className="text-gray-500" size={18} />
                </button>
              )}
              <button 
                className="bg-[#EAF0F7] border-0 px-4 py-2 rounded-lg text-sm"
                onClick={() => navigate(`/${window.location.pathname.split('/')[1]}/forms/${item.form}/questions/details/${item._id}`)}
              >
                View Details
              </button>
            </div>
          </div>
          
          <div className="flex flex-col flex-1">
            {item?.video && (
              <video 
                id={`video-${item._id}`}
                src={item.video}
                className="w-full h-[300px] object-cover rounded-xl mt-3"
                autoPlay
                muted
                loop
                playsInline
              />
            )}
              
            <div className="mb-0">
              <h3 className="text-lg font-medium text-gray-900 mb-0">{item.title}</h3>
              <div className={`relative ${expandedCards[item._id] ? '' : 'max-h-[48px] overflow-hidden'}`}>
                <p className="text-sm text-gray-500">
                  {item.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};





// Responses Card View Component
const ResponseCards = ({ data, onViewResponses, loading }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map((n) => (
          <div key={n} className="bg-white rounded-2xl border p-5 animate-pulse">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-200" />
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
            <div className="flex items-center mt-4 justify-between">
              <div className="w-24 h-7 bg-gray-200 rounded-full" />
              <div className="w-28 h-7 bg-gray-200 rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Use data directly since it's already formatted in the parent component
  if (!data || !data.length) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No responses found</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {data.map((item) => (
        <div key={item.key} className="bg-white rounded-2xl border p-5 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <UserGradientAvatar
              src={item.avatar}
              alt={item.respondentId || item.name}
              userId={item.userId || item.respondentKey}
              backgroundImageOverride={item.avatarBackground}
            />
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 mb-0 mt-0">
                {item.respondentId || item.name}
              </h3>
              <p className="text-sm text-gray-500 mb-0 mt-0">{item.email}</p>
            </div>
          </div>
          
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="bg-gray-50 p-2 rounded">
              <div className="text-xs text-gray-500">Questions</div>
              <div className="font-medium">{item.questions}</div>
            </div>
            <div className="bg-gray-50 p-2 rounded">
              <div className="text-xs text-gray-500">Responses</div>
              <div className="font-medium">{item.responses} ({item.percentage})</div>
            </div>
          </div>
          
          <div className="flex items-center mt-4 justify-between">
            <span className={`px-4 py-1.5 rounded-full text-sm ${
              item.status === 'Completed' 
                ? 'bg-emerald-500 text-white' 
                : 'border border-red-500 text-red-500'
            }`}>
              {item.status}
            </span>
            <button
              onClick={() => {
                if (onViewResponses) {
                  onViewResponses(item);
                }
              }}
              className="py-1.5 border-0 px-4 bg-[#EAF0F7] text-sm rounded-lg text-gray-700 hover:bg-gray-200 transition-colors"
            >
              View Responses
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};
const formatResponseData = (responses) => {
  if (!responses?.length) return [];
  
  return responses.map((response) => {
    try {
      let parsedResponse = {};
      try {
        parsedResponse = JSON.parse(response.response);
      } catch (e) {
        console.warn('Failed to parse response:', e);
        parsedResponse = {};
      }

      let userData = {
        key: response._id || 'fallback-key',
        status: response.step >= 5 ? 'Completed' : 'Incomplete',
        avatar: undefined,
        name: 'Anonymous User',
        email: 'No email provided'
      };

      // Extract user info from first step response
      if (parsedResponse.responses && response.step === 1) {
        userData = {
          ...userData,
          name: parsedResponse.responses['Full Name']?.value || 'Anonymous User',
          email: parsedResponse.responses['Email Address']?.value || 'No email provided',
        };
      }

      return userData;
    } catch (error) {
      console.warn('Error processing response:', error);
      // Return fallback data if anything fails
      return {
        key: response._id || 'fallback-key',
        status: 'Incomplete',
        avatar: undefined,
        name: 'Anonymous User',
        email: 'No email provided'
      };
    }
  });
};

export { QuestionCards, ResponseCards };